/**
 * How a stage is left: a guided flow's outcome and back/forward pair, or the
 * named move to the next stage.
 * Split from the stage body so neither file crosses the size gate.
 */
import React from 'react';
import { FmButton } from './FmButton';
import { GuidedStepOutcome, GuidedStepTransition } from './FmOperationalWorkspaceGuidance';
import { GuidedDockedTransition } from './FmOperationalGuidedChrome';
import type {
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';

/**
 * The move out of a stage: a guided flow offers one step back and one forward
 * (in the flow on the standard chrome, docked to the bottom edge on compact);
 * a non-linear workspace names the next stage when the stage declares it.
 */
export function StageTransition({
  descriptor,
  activeStage,
  testId,
  outcomePlaced,
  onStageChange,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStage: FmOperationalStageDescriptor;
  testId: string;
  /** The outcome already follows the step's dominant region. */
  outcomePlaced: boolean;
  onStageChange: (stageId: string) => void;
}): React.ReactElement | null {
  if (descriptor.guidance) {
    return (
      <>
        {outcomePlaced ? null : (
          <GuidedStepOutcome guidance={descriptor.guidance} testId={testId} />
        )}
        {descriptor.guidance.chrome === 'compact' ? (
          <GuidedDockedTransition
            guidance={descriptor.guidance}
            stages={descriptor.stages}
            activeStage={activeStage}
            onStageChange={onStageChange}
          />
        ) : (
          <GuidedStepTransition
            guidance={descriptor.guidance}
            stages={descriptor.stages}
            activeStage={activeStage}
            onStageChange={onStageChange}
          />
        )}
      </>
    );
  }
  const nextStage =
    descriptor.stages[descriptor.stages.findIndex((stage) => stage.id === activeStage.id) + 1];
  if (!nextStage || !(activeStage.transition || activeStage.transitionActionLabel)) return null;
  // A transition sentence points at the next stage, so it always carries the
  // control that goes there; without a stage-specific verb the control names
  // the stage it opens.
  return (
    <div className="fm-operational-workspace__transition">
      {activeStage.transition ? <p>{activeStage.transition}</p> : null}
      {nextStage.status !== 'blocked' ? (
        <FmButton
          variant={activeStage.transitionActionLabel ? 'primary' : 'bordered'}
          size="md"
          onClick={() => onStageChange(nextStage.id)}
          data-testid={`operational-transition-${nextStage.id}`}
        >
          {activeStage.transitionActionLabel ?? nextStage.label}
        </FmButton>
      ) : null}
    </div>
  );
}
